import { useQuery } from '@tanstack/react-query';
import { aiApi } from '@/api/ai';
import { Cross } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { getAiUsageMessage, getAiUsagePercent } from '@/lib/ai-usage';
import { cn } from '@/lib/utils';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';

const WARNING_THRESHOLD = 80;
const CRITICAL_THRESHOLD = 95;

type AiUsageNoticeProps = {
    tone?: 'surface' | 'inline';
    className?: string;
};

type AiUsageNoticeState = 'warning' | 'critical';

const getUsageState = (usagePercent: number): AiUsageNoticeState | null => {
    if (usagePercent >= CRITICAL_THRESHOLD) return 'critical';
    if (usagePercent >= WARNING_THRESHOLD) return 'warning';
    return null;
};

export default function AiUsageNotice({ tone = 'surface', className }: AiUsageNoticeProps) {
    const { dismissedAiUsageNoticeLevel, dismissAiUsageNotice } = useReportWizard();
    const { data: usage } = useQuery({
        queryKey: ['ai-usage'],
        queryFn: aiApi.getUsage,
        staleTime: 30_000,
    });

    const limit = usage?.limit ?? 0;
    const usagePercent = getAiUsagePercent(usage);
    const usageState = getUsageState(usagePercent);

    if (!usage || limit <= 0 || !usageState) return null;

    if (dismissedAiUsageNoticeLevel === usageState) {
        return null;
    }

    const message = getAiUsageMessage(usage);

    if (!message) return null;

    const toneClasses =
        tone === 'inline'
            ? 'rounded-md border border-border-strong/80 bg-surface px-3 py-2 text-xs leading-5 text-muted-foreground'
            : 'rounded-md border border-border bg-surface/70 px-3 py-2 text-xs leading-5 text-muted-foreground';
    const stateClasses =
        usageState === 'critical'
            ? tone === 'inline'
                ? 'border-border-strong bg-muted text-foreground'
                : 'text-foreground'
            : 'text-muted-foreground';

    return (
        <div
            className={cn(
                'flex items-start justify-between gap-3',
                toneClasses,
                stateClasses,
                className,
            )}
        >
            <p className="min-w-0 flex-1">
                <span className="font-medium text-foreground">Uso de IA:</span> {message}
            </p>
            <Button
                type="button"
                variant="ghost"
                size="iconXs"
                onClick={() => dismissAiUsageNotice(usageState)}
                aria-label="Fechar aviso de uso de IA"
                className="mt-0.5 shrink-0 rounded-sm text-muted-foreground hover:bg-transparent hover:text-muted-foreground [&_svg]:size-3.5"
            >
                <Cross />
            </Button>
        </div>
    );
}
